// src/App.jsx
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { AuthProvider } from './context/AuthContext';
import { UsersProvider } from './context/UsersContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { AssetsProvider } from './context/AssetsContext';
import { ToastProvider } from './context/ToastContext';
import { CalendarProvider } from './context/CalendarContext';
import { useState, useEffect, Suspense } from 'react';
import NavbarComponent from './components/NavbarComp';
import { publicRoutes, protectedRoutes, createProtectedRoute } from './config/routes';
import FooterComponent from './components/Footer';

// Custom loading component for better user experience
const LoadingFallback = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[800px]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
  </div>
);

const AppRoutes = () => {
    const location = useLocation();
    const isAuthRoute = location.pathname === '/' || location.pathname === '/register' || location.pathname === '/login';

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const getContentHeight = () => {
        const navbarHeight = 64;
        return isAuthRoute ? windowHeight : windowHeight - navbarHeight;
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-100 dark:bg-gray-800">
            {!isAuthRoute && (
                <NavbarComponent onToggleSidebar={toggleSidebar} />
           
            )}
            <div 
                className="flex flex-1 overflow-hidden bg-white dark:bg-gray-800"
                style={{ height: getContentHeight() + 'px' }}
            >
                <CalendarProvider>
                    {!isAuthRoute && (
                        <>
                            <div
                                className={`fixed inset-y-0 left-0 z-40 w-64 bg-gray-100 dark:bg-gray-800 transform transition-transform duration-200 ease-in-out overflow-y-auto ${
                                    isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                                } lg:static lg:translate-x-0`}
                                style={{ top: isAuthRoute ? '0' : '64px', height: isAuthRoute ? '100%' : 'calc(100% - 64px)' }}
                            >
                                <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                            </div>
                            {isSidebarOpen && (
                                <div
                                    className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
                                    onClick={toggleSidebar}
                                />
                            )}
                        </>
                    )}
                    {/* min-w-0: without it a flex item is at least as wide as its
                        content, so one long unbroken word (a list or task name)
                        stretches the page sideways instead of being truncated. */}
                    <div className="min-w-0 flex-1 overflow-y-auto">
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                {publicRoutes.map((route) => (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        element={route.element}
                                    />
                                ))}
                                {protectedRoutes.map((route) => (
                                    <Route
                                        key={route.path}
                                        {...createProtectedRoute(route)}
                                    />
                                ))}
                            </Routes>
                        </Suspense>
                    </div>
                </CalendarProvider>
            </div>
              {!isAuthRoute && (
                <FooterComponent />
           
            )}
        </div>
    );
};

function App() {
    return (
        <ToastProvider>
            <AssetsProvider>
                <NotificationsProvider>
                    <AuthProvider>
                        <UsersProvider>
                            <Router>
                                <AppRoutes />
                            </Router>
                        </UsersProvider>
                    </AuthProvider>
                </NotificationsProvider>
            </AssetsProvider>
        </ToastProvider>
    );
}

export default App;